/**
 * URL scrubbing for analytics (docs/investigations/posthog-analytics.md, secao 1.6).
 *
 * A raw `$current_url` on this product leaks two different things:
 *
 *  1. SECRETS. `/fit-in/<token>`, `/transferencia/<token>` and the password
 *     reset link carry a bearer-ish token in the path or query. Shipping those
 *     to a third party turns an analytics pipeline into a credential store.
 *  2. IDENTITY. `/pro/dra-fulana-cardiologista` names a real professional, and
 *     `/app/professional/patients/<uuid>` points at one specific patient.
 *
 * So every path is normalised to its ROUTE PATTERN before it leaves the
 * browser, and the entity id travels as an explicit event property instead
 * (`professional_id`), which is also what makes funnels group correctly.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Query params worth keeping: campaign attribution only, never a token. */
const SAFE_QUERY_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ref',
  'source',
]);

/** Path prefixes whose NEXT segment is a secret, not an id. */
const TOKEN_ROUTES = ['/fit-in/', '/transferencia/'];

/** Prefixes whose next segment is a public slug that still names a person. */
const SLUG_ROUTES = ['/pro/', '/dr/', '/doctor/', '/medico/'];

/**
 * `/app/professional/patients/3f2b…/notes` -> `/app/professional/patients/[id]/notes`
 * `/fit-in/abc123`                        -> `/fit-in/[token]`
 * `/medico/ana-silva/cardiologia/sp`      -> `/medico/[slug]/[especialidade]/[cidade]`
 */
export function sanitizePath(pathname: string): string {
  if (!pathname) return '/';

  for (const prefix of TOKEN_ROUTES) {
    if (pathname.startsWith(prefix)) return `${prefix}[token]`;
  }

  // The SEO route is three personal segments deep; collapse the whole tail.
  if (pathname.startsWith('/medico/')) {
    const parts = pathname.split('/').filter(Boolean); // medico, slug, esp, cidade
    if (parts.length >= 4) return '/medico/[slug]/[especialidade]/[cidade]';
  }

  return pathname
    .split('/')
    .map((segment, i, all) => {
      if (!segment) return segment;
      if (UUID_RE.test(segment)) return '[id]';
      // Numeric ids (rare here, but free to cover).
      if (/^\d+$/.test(segment)) return '[id]';
      // The segment right after a public-profile prefix is a person's slug.
      const parentPrefix = `/${all.slice(1, i).join('/')}/`;
      if (i >= 2 && SLUG_ROUTES.includes(parentPrefix)) return '[slug]';
      return segment;
    })
    .join('/');
}

/** Keeps only attribution params; everything else (tokens, e-mails) is dropped. */
export function sanitizeSearch(search: string): string {
  if (!search || search === '?') return '';
  const kept = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (SAFE_QUERY_KEYS.has(key)) kept.set(key, value);
  }
  const out = kept.toString();
  return out ? `?${out}` : '';
}

/** Full scrubbed URL for `$current_url`. Origin is kept (it is not a secret). */
export function sanitizeUrl(href: string): string {
  try {
    const url = new URL(href);
    return `${url.origin}${sanitizePath(url.pathname)}${sanitizeSearch(url.search)}`;
  } catch {
    return sanitizePath(href);
  }
}
